import React, { useCallback, useEffect, useState } from "react";
import { api, Group } from "../api/client";
import MessengerLinks from "../components/MessengerLinks";
import PageLoader from "../components/PageLoader";
import { generateCredentials } from "../utils/credentials";
import { CERT_ACCEPT, compressCertPhoto, isAcceptedCertType } from "../utils/certPhoto";

const EMPTY_FORM = {
  surname: "",
  name: "",
  login: "",
  password: "",
  birthDate: "",
  groupId: "",
  certExpiry: "",
};

type Credentials = { login: string; password: string };

export default function CreatePlayerPage() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [takenLogins, setTakenLogins] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Credentials | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [groupList, players] = await Promise.all([api.listGroups(), api.listPlayers()]);
      setGroups(groupList ?? []);
      setTakenLogins(new Set((players ?? []).map((p) => p.login)));
    } catch (err: any) {
      console.error("Failed to load groups and players", err);
      setError(err?.message || "Не удалось загрузить группы и игроков");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // A full-size photo held as an object URL stays in memory until revoked,
  // so the previous one is released whenever the file changes or the page closes.
  useEffect(() => {
    if (!photo) {
      setPhotoUrl(null);
      return;
    }
    const url = URL.createObjectURL(photo);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  // Like the app's toast: the confirmation goes away after a moment.
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const updateName = (field: "surname" | "name", value: string) => {
    setForm((f) => {
      const next = { ...f, [field]: value };
      // Same as the mobile app: login and password follow the name.
      const credentials = generateCredentials(next.surname, next.name);
      return credentials ? { ...next, ...credentials } : next;
    });
  };

  const updateField = (field: keyof typeof EMPTY_FORM, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const pickPhoto = (file: File | undefined) => {
    if (!file) {
      setPhoto(null);
      return;
    }
    if (!isAcceptedCertType(file.type)) {
      setPhoto(null);
      setFileInputKey((k) => k + 1);
      setError("Фото справки: подойдёт только JPEG, PNG или WebP");
      return;
    }
    setError(null);
    setPhoto(file);
  };

  const removePhoto = () => {
    setPhoto(null);
    setFileInputKey((k) => k + 1);
  };

  const canSubmit =
    !saving && [form.surname, form.name, form.login, form.password].every((v) => v.trim() !== "");

  const handleCreate = async () => {
    const login = form.login.trim();
    const password = form.password.trim();
    if (takenLogins.has(login)) {
      setError(`Логин ${login} уже занят — поменяйте его`);
      return;
    }

    setSaving(true);
    setError(null);
    // Upload only on submit, not on file pick, so an abandoned form leaves
    // no orphan file on the server.
    let certUrl: string | null = null;
    try {
      if (photo) {
        try {
          certUrl = await api.uploadImage(await compressCertPhoto(photo));
        } catch (err: any) {
          throw new Error(`Не удалось загрузить фото справки: ${err?.message || err}`);
        }
      }
      const player = await api.createPlayer({
        name: `${form.surname.trim()} ${form.name.trim()}`,
        birth_date: form.birthDate || null,
        login,
        password,
        group_id: form.groupId || null,
        medical_cert_expiry: form.certExpiry || null,
        medical_cert_url: certUrl,
      });
      setTakenLogins((taken) => new Set(taken).add(player.login));
      setCreated({ login: player.login, password: player.password || password });
      setCopied(false);
    } catch (err: any) {
      console.error("Failed to create player", err);
      setError(err?.message || "Не удалось создать ребёнка");
      // The photo went up just before the create failed, and nothing links to it.
      if (certUrl) {
        await api
          .deleteUploadedImage(certUrl)
          .catch((cleanupErr) => console.error("Failed to remove the certificate photo", cleanupErr));
      }
    } finally {
      setSaving(false);
    }
  };

  const credentialsLines = created
    ? [`Логин: ${created.login}`, `Пароль: ${created.password}`]
    : [];
  const credentialsText = credentialsLines.join("\n");
  const canShare = typeof navigator.share === "function";

  const copyCredentials = async () => {
    try {
      await navigator.clipboard.writeText(credentialsText);
      setCopied(true);
    } catch (err) {
      console.error("Failed to copy credentials", err);
      setError("Не удалось скопировать — выделите текст и скопируйте вручную");
    }
  };

  const shareCredentials = async () => {
    try {
      await navigator.share({ text: credentialsText });
    } catch (err: any) {
      // Closing the share sheet without picking a chat rejects with AbortError.
      if (err?.name === "AbortError") return;
      console.error("Failed to share credentials", err);
      setError("Не удалось переслать — скопируйте и отправьте вручную");
    }
  };

  const startOver = () => {
    setCreated(null);
    setForm(EMPTY_FORM);
    setPhoto(null);
    setFileInputKey((k) => k + 1);
    setError(null);
    setCopied(false);
  };

  if (loading) return <PageLoader />;

  return (
    <div className="page">
      <h2>Добавить игрока</h2>
      <p className="page-description">
        Создание ребёнка. Логин и пароль подставляются из фамилии и имени — так же,
        как в приложении, их можно поправить. После создания доступы можно
        скопировать и сразу отправить ребёнку или родителю.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      {created ? (
        <div className="mav-card cp-created">
          <div className="mav-card-title-row">
            <h3>Ребёнок создан</h3>
            <span className="mav-badge mav-badge-active">Активен</span>
          </div>
          <div className="cp-credentials-box">
            <p className="cp-credentials">
              {credentialsLines.map((line) => (
                <span key={line}>{line}</span>
              ))}
            </p>
            <button
              type="button"
              className="cp-copy"
              onClick={copyCredentials}
              aria-label="Скопировать логин и пароль"
              title="Скопировать логин и пароль"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                {copied ? (
                  <path d="M5 12.5l4.5 4.5L19 7.5" />
                ) : (
                  <>
                    <rect x="9" y="9" width="11" height="11" rx="2" />
                    <path d="M5 15V6a2 2 0 0 1 2-2h9" />
                  </>
                )}
              </svg>
            </button>
          </div>
          {copied && (
            <span className="mav-status-ok cp-copied" role="status">
              Логин и пароль скопированы
            </span>
          )}
          <div className="mav-actions">
            <div className="share-actions">
              {canShare && (
                <button type="button" className="mav-btn-secondary" onClick={shareCredentials}>
                  Переслать
                </button>
              )}
              <MessengerLinks text={credentialsText} />
            </div>
            <button type="button" className="mav-btn-secondary" onClick={startOver}>
              Создать ещё
            </button>
          </div>
        </div>
      ) : (
        <form
          className="mav-card cp-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) handleCreate();
          }}
        >
          <Field id="cp-surname" label="Фамилия *">
            <input
              id="cp-surname"
              className="mav-input"
              value={form.surname}
              onChange={(e) => updateName("surname", e.target.value)}
              autoComplete="off"
              disabled={saving}
            />
          </Field>
          <Field id="cp-name" label="Имя *">
            <input
              id="cp-name"
              className="mav-input"
              value={form.name}
              onChange={(e) => updateName("name", e.target.value)}
              autoComplete="off"
              disabled={saving}
            />
          </Field>
          <Field id="cp-login" label="Логин *">
            <input
              id="cp-login"
              className="mav-input"
              value={form.login}
              onChange={(e) => updateField("login", e.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={saving}
            />
          </Field>
          <Field id="cp-password" label="Пароль *">
            <input
              id="cp-password"
              className="mav-input"
              value={form.password}
              onChange={(e) => updateField("password", e.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={saving}
            />
          </Field>
          <Field id="cp-birth-date" label="Дата рождения">
            <input
              id="cp-birth-date"
              type="date"
              className="mav-input"
              value={form.birthDate}
              onChange={(e) => updateField("birthDate", e.target.value)}
              disabled={saving}
            />
          </Field>
          <Field id="cp-group" label="Группа">
            <select
              id="cp-group"
              className="mav-input"
              value={form.groupId}
              onChange={(e) => updateField("groupId", e.target.value)}
              disabled={saving}
            >
              <option value="">Без группы</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </Field>
          <Field id="cp-cert-expiry" label="Медкомиссия до">
            <input
              id="cp-cert-expiry"
              type="date"
              className="mav-input"
              value={form.certExpiry}
              onChange={(e) => updateField("certExpiry", e.target.value)}
              disabled={saving}
            />
          </Field>
          <Field id="cp-photo" label="Фото справки">
            <input
              key={fileInputKey}
              id="cp-photo"
              type="file"
              accept={CERT_ACCEPT}
              onChange={(e) => pickPhoto(e.target.files?.[0])}
              disabled={saving}
            />
            {photo && photoUrl ? (
              <figure className="cp-photo-preview">
                <img src={photoUrl} alt={`Фото справки: ${photo.name}`} />
                <figcaption>
                  <span>
                    {photo.name}, {formatSize(photo.size)}
                  </span>
                  <button
                    type="button"
                    className="mav-btn-secondary"
                    onClick={removePhoto}
                    disabled={saving}
                  >
                    Убрать фото
                  </button>
                </figcaption>
              </figure>
            ) : (
              <span className="mav-hint">
                JPEG, PNG или WebP — перед загрузкой сжимается до 2048 px
              </span>
            )}
          </Field>

          <div className="mav-actions cp-actions">
            <button type="submit" className="mav-btn-primary" disabled={!canSubmit}>
              {saving ? "Создаём…" : "Создать"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} МБ`;
}

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="cp-field">
      <label className="mav-input-label" htmlFor={id}>
        {label}
      </label>
      {children}
    </div>
  );
}
